// Multi-address chip input (To / Cc). Invalid entries stay in the field and are flagged.
import { X } from "lucide-react";
import { useTranslate } from "ra-core";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { parseRecipients } from "./composeModel";

type Props = {
  id: string;
  label: string;
  value: string[];
  onChange: (v: string[]) => void;
  /** Invalid entries still in the field; the drawer blocks Send while any remain. */
  onInvalidChange: (invalid: string[]) => void;
};

export const RecipientsInput = ({
  id,
  label,
  value,
  onChange,
  onInvalidChange,
}: Props) => {
  const t = useTranslate();
  const [draft, setDraft] = useState("");
  const [invalid, setInvalid] = useState<string[]>([]);
  const commit = () => {
    const { valid, invalid: bad } = parseRecipients(draft);
    if (valid.length) onChange([...new Set([...value, ...valid])]);
    setInvalid(bad);
    onInvalidChange(bad);
    setDraft(bad.join(" "));
  };
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex min-h-9 flex-wrap items-center gap-1 rounded-md border px-2 py-1 focus-within:ring-2 focus-within:ring-primary">
        {value.map((e) => (
          <Badge
            key={e}
            variant="secondary"
            className="max-w-full gap-1 break-all"
          >
            {e}
            <button
              type="button"
              className="rounded-sm p-0.5"
              aria-label={t("gavion.compose.remove_recipient", { email: e })}
              onClick={() => onChange(value.filter((x) => x !== e))}
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          </Badge>
        ))}
        <Input
          id={id}
          type="text"
          inputMode="email"
          // GAVION compose: no password-manager popup over the recipients (owner 2026-11-01, Bitwarden)
          autoComplete="off"
          data-bwignore="true"
          data-1p-ignore="true"
          data-lpignore="true"
          aria-invalid={invalid.length > 0}
          className="h-7 min-w-32 flex-1 border-0 p-0 shadow-none focus-visible:ring-0"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "," || e.key === ";") {
              e.preventDefault();
              commit();
            }
          }}
        />
      </div>
      {invalid.map((e) => (
        <p key={e} role="alert" className="text-xs text-destructive">
          {t("gavion.compose.invalid_email", { email: e })}
        </p>
      ))}
    </div>
  );
};
